import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle, Zap, Shield, Smartphone } from 'lucide-react';

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      category: "Product",
      icon: <Zap className="w-6 h-6" />,
      questions: [
        {
          question: "Why is the AI chip in only one of the shinpads? Why not both?",
          answer: "The AI chip is only in one of the shin pads because that's all you need to get real, meaningful insights. After speaking with players, coaches, and parents, the message was clear—keep it simple and don't overengineer. By including the chip in just one pad, we prioritize affordability, comfort, and simplicity—so athletes can focus on performance without unnecessary distractions. You still get a pair, but only one is smart, because that's what actually matters."
        },
        {
          question: "How long does the battery last?",
          answer: "Our smart shin pad has a battery life of up to 90 minutes of continuous play, which covers most training sessions and matches. The device includes rapid charging capabilities, reaching full charge in under 30 minutes."
        },
        {
          question: "Is the shin pad comfortable to wear?",
          answer: "Absolutely. Our smart shin pad is designed with comfort as a priority. The integrated technology adds minimal weight and bulk, and the ergonomic design ensures it fits naturally with your playing style."
        },
        {
          question: "What metrics does Cresento track?",
          answer: "Cresento tracks over 50 performance metrics including sprint speed, acceleration, deceleration, distance covered, number of touches, pass accuracy, shot power, and movement patterns. Our AI provides personalized insights based on your unique playing style."
        }
      ]
    },
    {
      category: "Technology",
      icon: <Smartphone className="w-6 h-6" />,
      questions: [
        {
          question: "How does the data sync to my phone?",
          answer: "Data syncs automatically via Bluetooth Low Energy (BLE) during breaks in play or immediately after your session. You can also sync via Wi-Fi for faster data transfer when reviewing detailed analytics."
        },
        {
          question: "Do I need an internet connection to use Cresento?",
          answer: "No internet connection is required during play. The shin pad stores data locally and syncs with your mobile app when connected. However, for advanced AI insights and cloud analytics, an internet connection is needed."
        },
        {
          question: "Is my data secure?",
          answer: "Yes, we take data privacy seriously. All data is encrypted during transmission and storage. You have full control over your data and can choose what to share with coaches or teammates."
        },
        {
          question: "What devices are compatible with the Cresento app?",
          answer: "The Cresento mobile app is available for iOS (iPhone 8 and newer) and Android (Android 8.0 and newer). The app works seamlessly with our smart shin pads via Bluetooth."
        }
      ]
    },
    {
      category: "Usage",
      icon: <Shield className="w-6 h-6" />,
      questions: [
        {
          question: "Can I use Cresento in official matches?",
          answer: "Yes, our smart shin pads meet all official football safety standards and regulations. They're approved for use in professional and amateur leagues worldwide."
        },
        {
          question: "How do I clean and maintain my Cresento shin pad?",
          answer: "The shin pad is water-resistant and can be wiped clean with a damp cloth. The removable inner padding is machine washable. Avoid submerging the electronic components in water."
        },
        {
          question: "What if my shin pad gets damaged during play?",
          answer: "Our shin pads are built to withstand the physical demands of football. They come with a 2-year warranty covering manufacturing defects and normal wear. Accidental damage protection is available as an optional add-on."
        },
        {
          question: "Can coaches access player data?",
          answer: "Players have full control over data sharing. You can choose to share specific metrics with coaches, teammates, or training partners through the mobile app. All sharing is permission-based and can be revoked at any time."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24 pt-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <HelpCircle className="w-16 h-16 text-cresento-accent mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to know about Cresento smart shin pads and our AI-powered football analytics platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/40 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            {faqs.map((category, categoryIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                {/* Category Header */}
                <div className="flex items-center mb-8">
                  <div className="text-cresento-accent mr-3">
                    {category.icon}
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-cresento-accent">
                    {category.category}
                  </h2>
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  {category.questions.map((faq, index) => {
                    const globalIndex = categoryIndex * 10 + index; // Ensure unique indices
                    const isActive = activeIndex === globalIndex;
                    
                    return (
                      <motion.div
                        key={globalIndex}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl border border-cresento-secondary/30 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-cresento-primary/10 transition-all duration-300"
                        >
                          <span className="text-lg font-semibold text-white pr-4">
                            {faq.question}
                          </span>
                          <div className="flex-shrink-0 text-cresento-accent">
                            {isActive ? (
                              <ChevronUp className="w-6 h-6" />
                            ) : (
                              <ChevronDown className="w-6 h-6" />
                            )}
                          </div>
                        </button>
                        
                        <motion.div
                          initial={false}
                          animate={{
                            height: isActive ? 'auto' : 0,
                            opacity: isActive ? 1 : 0
                          }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 pt-0">
                            <div className="border-t border-cresento-secondary/20 pt-4">
                              <p className="text-gray-300 leading-relaxed text-base sm:text-lg">
                                {faq.answer}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-r from-cresento-secondary to-cresento-accent">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white">
              Still Have Questions?
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              Our team is here to help you understand how Cresento can transform your game.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <HelpCircle className="mr-2 h-5 w-5" />
                Contact Support
              </motion.a>

              <motion.a
                href="mailto:support@cresento.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center"
              >
                <Smartphone className="mr-2 h-5 w-5" />
                Email Us
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;